import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, map, switchMap, takeUntil, tap } from 'rxjs';
import { QQQuestionnairePageViewModel } from 'src/app/models/qq-questionnaire-page.view-model';
import { QQQuestionnaireViewModel } from 'src/app/models/qq-questionnaire.view-model';
import { SharedStateService } from 'src/app/services/shared-state.service';
import { BeatYourBrokerHelper } from '../apg-beat-your-broker-helper';
import { QUICK_QUOTE } from '../apg-beat-your-broker.component';

@Component({
  templateUrl: './beat-your-broker.route.html',
  styleUrls: ['./beat-your-broker.route.scss'],
})
export class BeatYourBrokerRoute implements OnInit, OnDestroy {
  @Input() page$: Observable<QQQuestionnairePageViewModel>;
  @Input() firstPage: boolean;

  private dispose$ = new Subject();

  constructor(
    @Inject(QUICK_QUOTE) public quickquote$: BehaviorSubject<QQQuestionnaireViewModel>,
    private router: Router,
    private route: ActivatedRoute,
    private helper: BeatYourBrokerHelper,
    private sharedStateService: SharedStateService<QQQuestionnaireViewModel>
  ) { }

  ngOnInit() {
    this.page$ = combineLatest([
      this.quickquote$,
      this.route.params.pipe(
        map(({ page }) => parseInt(page))
      )])
      .pipe(
        tap(([qq]) => {
          this.sharedStateService.updateState(qq);
        }),
        map(([qq, p]) => {
          const page = qq.getPage(p);
          if (page != qq.currentPage) {
            qq.currentPage$.next(page);
          }

          return page
        }),
      );

    combineLatest([
      this.quickquote$,
      this.page$.pipe(
        switchMap(x => x.onNext)
      )
    ]).pipe(
      switchMap(([qq, page]) => this.helper.getNextPage(qq, page)),
      tap(x => this.helper.allowPage(x)),
      tap(x => this.router.navigate(['/coverchecker', x])),
      takeUntil(this.dispose$)
    ).subscribe();

    combineLatest([
      this.quickquote$,
      this.page$.pipe(
        switchMap(x => x.onPrev)
      )
    ]).pipe(
      switchMap(([qq, page]) => this.helper.getPrevPage(qq, page)),
      tap(x => this.router.navigate(['/coverchecker', x])),
    ).subscribe();

  }

  ngOnDestroy(): void {
    this.dispose$.next(null);
  }

  open(link: string) {
    if (link == 'CoverCalculator')
      this.router.navigateByUrl('/coverCalculator');
    else
      window.location.href = link;
  }

  async saveOtp(qq: QQQuestionnaireViewModel, page: QQQuestionnairePageViewModel, code: string) {
    qq.saveAnswer('OtpCode', code);
    const nextPage = await firstValueFrom(this.helper.getNextPage(qq, page));
    this.helper.allowPage(nextPage);
    this.router.navigate(['/coverchecker', nextPage], { replaceUrl: true });
  }

  async saveContactDate(qq: QQQuestionnaireViewModel, page: QQQuestionnairePageViewModel, dateTime: string) {
    qq.saveAnswer('PreferredContactDate', dateTime);
    const nextPage = await firstValueFrom(this.helper.getNextPage(qq, page));
    this.helper.allowPage(nextPage);
    this.router.navigate(['/coverchecker', nextPage], { replaceUrl: true });
  }

  async navigateBack(qq: QQQuestionnaireViewModel, page: QQQuestionnairePageViewModel, count: number) {
    const prevPage = await firstValueFrom(this.helper.getPrevPage(qq, page, count));
    this.router.navigate(['/coverchecker', prevPage]);
  }
}
